import type { SearchSourceType } from '@affine/core/modules/docs-search';
import {
  type PdfPageSearchQuery,
  pdfPageSearchQuery,
  ServerFeature,
} from '@affine/graphql';
import type { I18nString } from '@affine/i18n';
import type { IndexerPreferOptions } from '@affine/nbstore';
import {
  AttachmentIcon,
  BlockIcon,
  DatabaseTableViewIcon,
  EdgelessIcon,
  ImageIcon,
  LinkIcon,
  PageIcon,
} from '@blocksuite/icons/rc';
import {
  type Effect,
  effect,
  Entity,
  LiveData,
  onComplete,
  onStart,
} from '@toeverything/infra';
import { truncate } from 'lodash-es';
import {
  catchError,
  debounceTime,
  EMPTY,
  from,
  map,
  merge,
  of,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';

import type { WorkspaceServerService } from '../../cloud';
import type { DocsService } from '../../doc';
import type { DocDisplayMetaService } from '../../doc-display-meta';
import type { DocsSearchService } from '../../docs-search';
import { getDocsSearchLocationIdentity } from '../../docs-search/services/source-navigation';
import type { FeatureFlagService } from '../../feature-flag';
import type { WorkspaceService } from '../../workspace';
import type { QuickSearchSession } from '../providers/quick-search-provider';
import type { QuickSearchItem } from '../types/item';
import {
  type DocsPayload,
  type DocsSearchResult,
  type PdfPageSearchHit,
} from './docs-pdf-search';
import {
  BLOCK_SOURCES_PER_DOC_LIMIT,
  type BlockRow,
  blockSourcesListKey,
  buildBlockRows,
  buildSearchDocBuckets,
  countPdfAttachmentChildren,
  countSearchDocBucketChildren,
  PDF_PAGES_PER_ATTACHMENT_LIMIT,
  pdfPagesListKey,
  type SearchDocBucket,
  splitForLoadMore,
} from './docs-search-grouping';
import {
  DOCS_SEARCH_CLOUD_FALLBACK_STATUS,
  type DocsSearchModeFlags,
  resolvePreferredDocsSearchMode,
  statusForDocsSearchMode,
} from './docs-search-status';
import {
  createDedupedStaleResultNudge,
  type DedupedStaleResultNudge,
  filterStaleLoadedBlockResults,
} from './docs-stale-results';

const PDF_QUICK_SEARCH_LIMIT = 20;
const PDF_SEARCH_FAILURE_COOLDOWN_MS = 30_000;
const DOCS_SEARCH_DEBOUNCE_MS = 350;

export function mergeDocsSearchResults(
  primary: DocsSearchResult[],
  secondary: DocsSearchResult[]
): DocsSearchResult[] {
  const seen = new Map<string, number>();
  const merged: DocsSearchResult[] = [];
  for (const item of [...primary, ...secondary]) {
    const key = getDocsSearchLocationIdentity(item);
    const existingIndex = seen.get(key);
    if (existingIndex !== undefined) {
      merged[existingIndex] = enrichDocsSearchResult(
        merged[existingIndex],
        item
      );
      continue;
    }
    seen.set(key, merged.length);
    merged.push(item);
  }
  return merged;
}

export function filterIncompleteCloudDatabaseRowHits(
  results: DocsSearchResult[]
): DocsSearchResult[] {
  return results.filter(
    result =>
      result.sourceType !== 'database' ||
      !result.databaseRowId ||
      !!result.databaseRowShardDocId
  );
}

function enrichDocsSearchResult(
  primary: DocsSearchResult,
  secondary: DocsSearchResult
): DocsSearchResult {
  return {
    ...primary,
    title: primary.title || secondary.title,
    blockContent: preferQueryFocusedPreview(
      primary.blockContent,
      secondary.blockContent
    ),
    elementId: primary.elementId ?? secondary.elementId,
    parentBlockId: primary.parentBlockId ?? secondary.parentBlockId,
    parentFlavour: primary.parentFlavour ?? secondary.parentFlavour,
    noteBlockId: primary.noteBlockId ?? secondary.noteBlockId,
    displayMode: primary.displayMode ?? secondary.displayMode,
    databaseId: primary.databaseId ?? secondary.databaseId,
    databaseRowId: primary.databaseRowId ?? secondary.databaseRowId,
    databaseRowShardDocId:
      primary.databaseRowShardDocId ?? secondary.databaseRowShardDocId,
    databaseColumnId: primary.databaseColumnId ?? secondary.databaseColumnId,
    databaseTitle: primary.databaseTitle || secondary.databaseTitle,
    databaseRowLabel: primary.databaseRowLabel || secondary.databaseRowLabel,
    databaseColumnName:
      primary.databaseColumnName || secondary.databaseColumnName,
    visibility: primary.visibility ?? secondary.visibility,
    technicalSourceKind:
      primary.technicalSourceKind ?? secondary.technicalSourceKind,
  };
}

function preferQueryFocusedPreview(
  primary: string | undefined,
  secondary: string | undefined
) {
  if (!primary) return secondary;
  if (!secondary) return primary;
  if (!primary.includes('<b>') && secondary.includes('<b>')) return secondary;
  return primary;
}

export function createVisibleQuerySnippet(
  content: string | undefined,
  query: string
) {
  if (!content) return content;
  const normalizedQuery = query.trim();
  if (!normalizedQuery) return content;

  const lowerContent = content.toLowerCase();
  const markedQuery = `<b>${normalizedQuery.toLowerCase()}</b>`;
  const markedIndex = lowerContent.indexOf(markedQuery);
  const matchIndex =
    markedIndex >= 0
      ? markedIndex
      : lowerContent.indexOf(normalizedQuery.toLowerCase());
  if (matchIndex < 0) return content;

  const matchLength =
    markedIndex >= 0 ? markedQuery.length : normalizedQuery.length;
  // Quick Search renders the snippet on one ellipsized line. Keep the matched
  // term near the left edge so its highlight remains visibly inside the row.
  const start = Math.max(0, matchIndex - 24);
  const end = Math.min(content.length, matchIndex + matchLength + 72);
  const snippet = content.slice(start, end);
  const focused =
    markedIndex >= 0
      ? snippet
      : snippet.replace(
          new RegExp(escapeRegExp(normalizedQuery), 'i'),
          match => `<b>${match}</b>`
        );
  return `${start > 0 ? '...' : ''}${focused}${
    end < content.length ? '...' : ''
  }`;
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Leading icon per source type so kinds are visually distinguishable. */
const SOURCE_TYPE_ICON: Record<SearchSourceType, QuickSearchItem['icon']> = {
  doc: PageIcon,
  text: BlockIcon,
  image: ImageIcon,
  edgeless: EdgelessIcon,
  attachment: AttachmentIcon,
  embed: LinkIcon,
  database: DatabaseTableViewIcon,
  pdf: AttachmentIcon,
};

/** i18n key for the explicit source-type badge shown on every source row. */
const SOURCE_TYPE_LABEL_KEY: Record<SearchSourceType, string> = {
  doc: 'com.streamlain.quicksearch.sourceType.doc',
  text: 'com.streamlain.quicksearch.sourceType.text',
  image: 'com.streamlain.quicksearch.sourceType.image',
  edgeless: 'com.streamlain.quicksearch.sourceType.edgeless',
  attachment: 'com.streamlain.quicksearch.sourceType.attachment',
  embed: 'com.streamlain.quicksearch.sourceType.embed',
  database: 'com.streamlain.quicksearch.sourceType.database',
  pdf: 'com.streamlain.quicksearch.sourceType.pdf',
};

export class DocsQuickSearchSession
  extends Entity
  implements QuickSearchSession<'docs', DocsPayload>
{
  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly docsSearchService: DocsSearchService,
    private readonly docsService: DocsService,
    private readonly docDisplayMetaService: DocDisplayMetaService,
    private readonly featureFlagService: FeatureFlagService
  ) {
    super();
    this.staleResultNudge = createDedupedStaleResultNudge({
      addPriority: (docId, priority) =>
        this.docsSearchService.indexer.addPriority(docId, priority),
      warn: result => {
        console.warn(
          '[DocsQuickSearchSession] Filtering stale search result for missing loaded-doc block',
          result
        );
      },
    });
  }

  private readonly isSupportServerIndexer = () =>
    this.workspaceServerService.server?.config$.value.features.includes(
      ServerFeature.Indexer
    ) ?? false;

  private readonly isEnableBatterySaveMode = () =>
    this.featureFlagService.flags.enable_battery_save_mode.value;

  private readonly isIndexerLoading$ = this.docsSearchService.indexerState$.map(
    ({ completed }) => {
      return !completed;
    }
  );

  private readonly isQueryLoading$ = new LiveData(false);

  /**
   * Unlike `isLoading$`, this stays false while a cloud query is showing its
   * local-first snapshot and only turns true after the remote merge (or the
   * explicit local fallback) has arrived. Compact Quick Search may keep using
   * progressive results; Advanced Search uses this signal to avoid presenting
   * a partial result count as a finished result set.
   */
  readonly isSettled$ = new LiveData(true);

  isCloudWorkspace = this.workspaceService.workspace.flavour !== 'local';

  isLoading$ = LiveData.computed(get => {
    return (
      (this.isCloudWorkspace ? false : get(this.isIndexerLoading$)) ||
      get(this.isQueryLoading$)
    );
  });

  error$ = new LiveData<any>(null);

  status$ = new LiveData<I18nString | null>(null);

  lastQuery = '';

  items$ = new LiveData<QuickSearchItem<'docs', DocsPayload>[]>([]);

  private pdfSearchFailedUntil = 0;

  private readonly isOnline = () =>
    typeof navigator === 'undefined' || navigator.onLine;

  private readonly isPdfSearchInCooldown = () =>
    this.pdfSearchFailedUntil > Date.now();

  private readonly staleResultNudge: DedupedStaleResultNudge;

  private readonly modeFlags = (): DocsSearchModeFlags => ({
    isCloudWorkspace: this.isCloudWorkspace,
    isOnline: this.isOnline(),
    isServerIndexerSupported: this.isSupportServerIndexer(),
    isBatterySaveMode: this.isEnableBatterySaveMode(),
    isPdfSearchInCooldown: this.isPdfSearchInCooldown(),
  });

  private readonly resolveDocsSearchPrefer = (): IndexerPreferOptions =>
    resolvePreferredDocsSearchMode(this.modeFlags());

  private readonly statusForSearchMode = (
    prefer: IndexerPreferOptions
  ): I18nString | null => statusForDocsSearchMode(this.modeFlags(), prefer);

  private readonly canSearchPdfPages = (prefer: IndexerPreferOptions) =>
    prefer === 'remote' &&
    this.isSupportServerIndexer() &&
    !this.isEnableBatterySaveMode() &&
    !this.isPdfSearchInCooldown();

  private readonly searchPdfPages = async (
    query: string,
    prefer: IndexerPreferOptions
  ): Promise<PdfPageSearchHit[]> => {
    if (!this.canSearchPdfPages(prefer)) return [];

    const server = this.workspaceServerService.server;
    if (!server) return [];

    const result: PdfPageSearchQuery = await server.gql({
      query: pdfPageSearchQuery,
      variables: {
        id: this.workspaceService.workspace.id,
        input: {
          keyword: query,
          limit: PDF_QUICK_SEARCH_LIMIT,
        },
      },
    });

    return result.workspace.pdfSearch;
  };

  private readonly createGroup = (query: string) => ({
    id: 'docs',
    label: {
      i18nKey: 'com.streamlain.quicksearch.group.searchfor',
      options: { query: truncate(query) },
    },
    score: 5,
  });

  private readonly getDocRecord = (docId: string) => {
    const docRecord = this.docsService.list.doc$(docId).value;
    return docRecord ?? null;
  };

  private readonly createItems = (
    query: string,
    docs: DocsSearchResult[],
    pdfHits: PdfPageSearchHit[],
    expandedDocs: ReadonlySet<string>
  ) => {
    const group = this.createGroup(query);
    // Keep badges and expanded rows on the same filtered local/remote set.
    // Otherwise stale backend rows inflate the document count but disappear
    // from the expanded list.
    const displayDocs = filterStaleLoadedBlockResults(query, docs, {
      onStaleResult: this.staleResultNudge.nudge,
      resolveBlockStatus: this.resolveBlockStatusForList,
    });
    return buildSearchDocBuckets(displayDocs, pdfHits).flatMap(bucket =>
      this.createDocBucketItems(query, bucket, expandedDocs, group)
    );
  };

  private readonly createDocBucketItems = (
    query: string,
    bucket: SearchDocBucket,
    expandedDocs: ReadonlySet<string>,
    group: ReturnType<typeof this.createGroup>
  ) => {
    const { block, pdf } = bucket;
    if (pdf && block) {
      return this.createMixedDocItems(
        query,
        { ...bucket, block, pdf },
        expandedDocs,
        group
      );
    }

    if (pdf) {
      return this.createPdfDocItems(query, pdf, expandedDocs, group);
    }

    if (block) {
      return this.createBlockItems(query, block.sources, group);
    }

    return [];
  };

  private readonly createPdfDocItems = (
    query: string,
    docAgg: NonNullable<SearchDocBucket['pdf']>,
    expandedDocs: ReadonlySet<string>,
    group: ReturnType<typeof this.createGroup>
  ) => {
    const docRecord = this.getDocRecord(docAgg.docId);
    if (!docRecord) return [];
    const { title, icon, updatedDate } =
      this.docDisplayMetaService.getDocDisplayMeta(docRecord);
    const isDocExpanded = expandedDocs.has(docAgg.docId);
    const items: QuickSearchItem<'docs', DocsPayload>[] = [
      {
        id: `pdf-doc:${docAgg.docId}:${query}`,
        source: 'docs',
        group,
        label: {
          title,
          subTitle: docAgg.bestHit.snippet,
        },
        score: docAgg.bestHit.score,
        icon,
        timestamp: updatedDate,
        expanded: isDocExpanded,
        badge: pdfHitBadge(docAgg.bestHit, {
          i18nKey:
            docAgg.attachments.length === 1
              ? 'com.streamlain.quicksearch.pdf.docHits.single'
              : 'com.streamlain.quicksearch.pdf.docHits.multi',
          options: {
            hits: countSearchDocBucketChildren({
              docId: docAgg.docId,
              score: docAgg.bestHit.score,
              sourceTypes: ['pdf'],
              pdf: docAgg,
            }),
            attachments: docAgg.attachments.length,
          },
        }),
        payload: {
          kind: 'pdf-doc-header',
          docId: docAgg.docId,
          attachmentBlockId: docAgg.bestHit.attachmentBlockId,
        },
        beforeSubmit: () => {
          this.toggleDocExpand(docAgg.docId);
          return false;
        },
      },
    ];

    if (isDocExpanded) {
      items.push(...this.createPdfAttachmentItems(query, docAgg, group));
    }

    return items;
  };

  private readonly createMixedDocItems = (
    query: string,
    bucket: SearchDocBucket & {
      pdf: NonNullable<SearchDocBucket['pdf']>;
      block: NonNullable<SearchDocBucket['block']>;
    },
    expandedDocs: ReadonlySet<string>,
    group: ReturnType<typeof this.createGroup>
  ) => {
    const docRecord = this.getDocRecord(bucket.docId);
    if (!docRecord) return [];
    const { title, icon, updatedDate } =
      this.docDisplayMetaService.getDocDisplayMeta(docRecord);
    const isDocExpanded = expandedDocs.has(bucket.docId);
    const bestSubtitle =
      bucket.pdf.bestHit.score >= bucket.block.best.score
        ? bucket.pdf.bestHit.snippet
        : bucket.block.best.blockContent;
    const items: QuickSearchItem<'docs', DocsPayload>[] = [
      {
        id: `mixed-doc:${bucket.docId}:${query}`,
        source: 'docs',
        group,
        label: {
          title,
          subTitle: bestSubtitle,
        },
        score: bucket.score,
        icon,
        timestamp: updatedDate,
        expanded: isDocExpanded,
        badge: pdfHitBadge(bucket.pdf.bestHit, {
          i18nKey: 'com.streamlain.quicksearch.block.docHits',
          options: {
            count: countSearchDocBucketChildren(bucket),
          },
        }),
        payload: {
          docId: bucket.docId,
          title,
          sourceTypes: bucket.sourceTypes,
        },
        beforeSubmit: () => {
          this.toggleDocExpand(bucket.docId);
          return false;
        },
      },
    ];

    if (isDocExpanded) {
      items.push(...this.createPdfAttachmentItems(query, bucket.pdf, group));
      items.push(
        ...this.createNestedBlockSourceItems(
          query,
          bucket.block.sources,
          bucket.docId,
          group
        )
      );
    }

    return items;
  };

  private readonly createPdfAttachmentItems = (
    query: string,
    docAgg: NonNullable<SearchDocBucket['pdf']>,
    group: ReturnType<typeof this.createGroup>
  ) => {
    const items: QuickSearchItem<'docs', DocsPayload>[] = [];

    docAgg.attachments.forEach((attachment, attachmentIndex) => {
      const attachmentKey = `${docAgg.docId}:${attachment.attachmentBlockId}`;
      const isAttachmentExpanded = this.expandedAttachments.has(attachmentKey);

      items.push({
        id: `pdf-attachment:${attachmentKey}:${query}`,
        source: 'docs',
        group,
        label: {
          title: {
            i18nKey: 'com.streamlain.quicksearch.pdf.attachmentTitle',
            options: { index: attachmentIndex + 1 },
          },
          subTitle: attachment.bestHit.snippet,
        },
        score: attachment.bestHit.score,
        icon: AttachmentIcon,
        indent: 1,
        expanded: isAttachmentExpanded,
        badge: pdfHitBadge(attachment.bestHit, {
          i18nKey:
            attachment.pages.length === 1
              ? 'com.streamlain.quicksearch.pdf.attachmentHits.single'
              : 'com.streamlain.quicksearch.pdf.attachmentHits.multi',
          options: {
            hits: countPdfAttachmentChildren(attachment),
            pages: attachment.pages.length,
          },
        }),
        payload: {
          kind: 'pdf-attachment-header',
          docId: docAgg.docId,
          attachmentBlockId: attachment.attachmentBlockId,
        },
        beforeSubmit: () => {
          this.toggleAttachmentExpand(attachmentKey);
          return false;
        },
      });

      if (!isAttachmentExpanded) return;

      const listKey = pdfPagesListKey(
        docAgg.docId,
        attachment.attachmentBlockId
      );
      const { visible, hidden } = splitForLoadMore(
        attachment.pages,
        PDF_PAGES_PER_ATTACHMENT_LIMIT,
        this.expandedLists.has(listKey)
      );
      for (const page of visible) {
        items.push({
          id: `pdf-page:${docAgg.docId}:${page.attachmentBlockId}:${page.pageIndex}:${query}`,
          source: 'docs',
          group,
          label: {
            title: {
              i18nKey: 'com.streamlain.quicksearch.pdf.pageTitle',
              options: { page: page.pageIndex + 1 },
            },
            subTitle: page.bestHit.snippet,
          },
          score: page.bestHit.score,
          icon: PageIcon,
          indent: 2,
          badge: pdfHitBadge(
            page.bestHit,
            page.hitCount > 1
              ? {
                  i18nKey: 'com.streamlain.quicksearch.pdf.pageHits',
                  options: { count: page.hitCount },
                }
              : undefined
          ),
          payload: {
            kind: 'pdf-page',
            docId: docAgg.docId,
            attachmentBlockId: page.attachmentBlockId,
            pageIndex: page.pageIndex,
            query,
            blockContent: page.bestHit.snippet,
            rects: page.bestHit.rects,
          },
        });
      }
      if (hidden > 0) {
        items.push(
          this.createLoadMoreItem(
            `pdf-page-more:${attachmentKey}:${query}`,
            listKey,
            docAgg.docId,
            hidden,
            2,
            group,
            ['pdf']
          )
        );
      }
    });

    return items;
  };

  private readonly createNestedBlockSourceItems = (
    query: string,
    sources: DocsSearchResult[],
    docId: string,
    group: ReturnType<typeof this.createGroup>
  ) => {
    const listKey = blockSourcesListKey(docId);
    const { visible, hidden } = splitForLoadMore(
      sources,
      BLOCK_SOURCES_PER_DOC_LIMIT,
      this.expandedLists.has(listKey)
    );
    const hiddenSources = sources.slice(visible.length);
    const items = visible
      .map(source =>
        this.blockRowToItem(
          query,
          {
            kind: 'block-source',
            docId: source.docId,
            title: source.title,
            blockId: source.blockId,
            ...(source.elementId ? { elementId: source.elementId } : {}),
            parentBlockId: source.parentBlockId,
            parentFlavour: source.parentFlavour,
            noteBlockId: source.noteBlockId,
            displayMode: source.displayMode,
            sourceType: source.sourceType,
            snippet: source.blockContent,
            databaseId: source.databaseId,
            databaseRowId: source.databaseRowId,
            databaseRowShardDocId: source.databaseRowShardDocId,
            databaseColumnId: source.databaseColumnId,
            databaseTitle: source.databaseTitle,
            databaseRowLabel: source.databaseRowLabel,
            databaseColumnName: source.databaseColumnName,
            visibility: source.visibility,
            technicalSourceKind: source.technicalSourceKind,
            score: source.score,
            indent: 1,
          },
          group
        )
      )
      .filter(
        (item): item is QuickSearchItem<'docs', DocsPayload> => item !== null
      );

    if (hidden > 0) {
      items.push(
        this.createLoadMoreItem(
          `block-more:${docId}:${query}`,
          listKey,
          docId,
          hidden,
          1,
          group,
          uniquePayloadSourceTypes(hiddenSources)
        )
      );
    }

    return items;
  };

  private readonly createBlockItems = (
    query: string,
    docs: DocsSearchResult[],
    group: ReturnType<typeof this.createGroup>
  ) => {
    const rows = buildBlockRows(docs, {
      expandedDocs: this.expandedBlockDocs,
      expandedLists: this.expandedLists,
    });
    const items: QuickSearchItem<'docs', DocsPayload>[] = [];
    for (const row of rows) {
      const item = this.blockRowToItem(query, row, group);
      if (item) items.push(item);
    }
    return items;
  };

  private readonly resolveBlockStatusForList = (
    docId: string,
    blockId: string
  ) => {
    const loaded = this.docsService.loaded(docId);
    if (!loaded) return 'unknown';
    try {
      return loaded.doc.blockSuiteDoc.getBlock(blockId) ? 'exists' : 'missing';
    } finally {
      loaded.release();
    }
  };

  private readonly blockRowToItem = (
    query: string,
    row: BlockRow,
    group: ReturnType<typeof this.createGroup>
  ): QuickSearchItem<'docs', DocsPayload> | null => {
    if (row.kind === 'block-load-more') {
      return this.createLoadMoreItem(
        `block-more:${row.docId}:${query}`,
        row.listKey,
        row.docId,
        row.hidden,
        1,
        group,
        row.sourceTypes
      );
    }

    const docRecord = this.getDocRecord(row.docId);
    if (!docRecord) return null;
    const { title, icon, updatedDate } =
      this.docDisplayMetaService.getDocDisplayMeta(docRecord);

    if (row.kind === 'block-doc-header') {
      const isExpanded = this.expandedBlockDocs.has(row.docId);
      return {
        id: `block-doc:${row.docId}:${query}`,
        source: 'docs',
        group,
        label: {
          title: preferHighlightedTitle(row.bestTitle, title),
          subTitle: row.bestSnippet,
        },
        score: row.score,
        icon,
        timestamp: updatedDate,
        expanded: isExpanded,
        badge: {
          i18nKey: 'com.streamlain.quicksearch.block.docHits',
          options: { count: row.count },
        },
        payload: { docId: row.docId, sourceTypes: row.sourceTypes },
        beforeSubmit: () => {
          this.toggleBlockDocExpand(row.docId);
          return false;
        },
      };
    }

    // block-source. Standalone rows stand in for the whole doc, so they keep
    // the doc's (custom) icon + title for provenance; nested rows sit under a
    // doc header already showing that, so they lead with the source-type icon
    // and the snippet to tell the individual hits apart.
    const isStandalone = row.indent === 0;
    const visibleSnippet = createVisibleQuerySnippet(row.snippet, query);
    return {
      id:
        'doc:' +
        row.sourceType +
        ':' +
        row.docId +
        (row.blockId ? ':' + row.blockId : '') +
        (row.elementId ? ':' + row.elementId : '') +
        (row.databaseId ? ':' + row.databaseId : '') +
        (row.databaseRowId ? ':' + row.databaseRowId : '') +
        (row.databaseColumnId ? ':' + row.databaseColumnId : '') +
        ':' +
        query,
      source: 'docs',
      group,
      label: {
        title: isStandalone
          ? preferHighlightedTitle(row.title, title)
          : (visibleSnippet ?? title),
        subTitle: isStandalone ? visibleSnippet : undefined,
      },
      score: row.score,
      icon: isStandalone ? icon : SOURCE_TYPE_ICON[row.sourceType],
      indent: isStandalone ? undefined : 1,
      timestamp: isStandalone ? updatedDate : undefined,
      badge: {
        i18nKey:
          row.technicalSourceKind === 'mail_storage'
            ? 'com.streamlain.quicksearch.technical.mailStorage'
            : SOURCE_TYPE_LABEL_KEY[row.sourceType],
      },
      payload: {
        docId: row.docId,
        title,
        blockId: row.blockId,
        elementId: row.elementId,
        parentBlockId: row.parentBlockId,
        parentFlavour: row.parentFlavour,
        noteBlockId: row.noteBlockId,
        displayMode: row.displayMode,
        blockContent: visibleSnippet,
        sourceType: row.sourceType,
        sourceTypes: [row.sourceType],
        databaseId: row.databaseId,
        databaseRowId: row.databaseRowId,
        databaseRowShardDocId: row.databaseRowShardDocId,
        databaseColumnId: row.databaseColumnId,
        visibility: row.visibility,
        technicalSourceKind: row.technicalSourceKind,
      },
    };
  };

  private readonly createLoadMoreItem = (
    id: string,
    listKey: string,
    docId: string,
    hidden: number,
    indent: 1 | 2,
    group: ReturnType<typeof this.createGroup>,
    sourceTypes?: SearchSourceType[]
  ): QuickSearchItem<'docs', DocsPayload> => ({
    id,
    source: 'docs',
    group,
    label: {
      i18nKey: 'com.streamlain.quicksearch.loadMore',
      options: { count: hidden },
    },
    score: 0,
    indent,
    payload: { docId, sourceTypes },
    beforeSubmit: () => {
      this.toggleListExpand(listKey);
      return false;
    },
  });

  private cache: {
    query: string;
    docs: DocsSearchResult[];
    pdfHits: PdfPageSearchHit[];
  } | null = null;
  private readonly expandedDocs = new Set<string>();
  private readonly expandedAttachments = new Set<string>();
  private readonly expandedBlockDocs = new Set<string>();
  private readonly expandedLists = new Set<string>();

  private readonly resetExpansion = () => {
    this.expandedDocs.clear();
    this.expandedAttachments.clear();
    this.expandedBlockDocs.clear();
    this.expandedLists.clear();
  };

  private readonly publishItems = () => {
    if (!this.cache) {
      this.items$.next([]);
      return;
    }
    this.items$.next(
      this.createItems(
        this.cache.query,
        this.cache.docs,
        this.cache.pdfHits,
        this.expandedDocs
      )
    );
  };

  private readonly toggleSet = (set: Set<string>, key: string) => {
    if (set.has(key)) set.delete(key);
    else set.add(key);
    this.publishItems();
  };

  private readonly toggleDocExpand = (docId: string) =>
    this.toggleSet(this.expandedDocs, docId);

  private readonly toggleAttachmentExpand = (attachmentKey: string) =>
    this.toggleSet(this.expandedAttachments, attachmentKey);

  private readonly toggleBlockDocExpand = (docId: string) =>
    this.toggleSet(this.expandedBlockDocs, docId);

  private readonly toggleListExpand = (listKey: string) =>
    this.toggleSet(this.expandedLists, listKey);

  private readonly searchDocs = (query: string) => {
    const prefer = this.resolveDocsSearchPrefer();
    this.status$.next(this.statusForSearchMode(prefer));
    if (prefer !== 'remote') {
      return this.docsSearchService
        .search$(query, {
          prefer,
        })
        .pipe(map(docs => ({ docs, prefer, settled: true })));
    }

    const localPrefer = 'local' as const;
    const localSearch$ = this.docsSearchService
      .search$(query, {
        prefer: localPrefer,
      })
      .pipe(
        catchError(() => of([] as DocsSearchResult[])),
        // In cloud workspaces, the local index can publish a database-cell hit
        // before the server returns its exact row-shard locator. Do not expose
        // that intermediate hit as a clickable result.
        map(filterIncompleteCloudDatabaseRowHits),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    return merge(
      localSearch$.pipe(
        map(docs => ({ docs, prefer: localPrefer, settled: false }))
      ),
      this.docsSearchService
        .search$(query, {
          prefer,
        })
        .pipe(
          switchMap(remoteDocs =>
            localSearch$.pipe(
              map(localDocs => ({
                docs: mergeDocsSearchResults(localDocs, remoteDocs),
                prefer,
                settled: true,
              }))
            )
          ),
          catchError(() => {
            this.status$.next(DOCS_SEARCH_CLOUD_FALLBACK_STATUS);
            return localSearch$.pipe(
              map(docs => ({ docs, prefer: localPrefer, settled: true }))
            );
          })
        )
    );
  };

  private readonly queryEffect = effect(
    tap((query: string) => {
      const queryChanged = query !== this.lastQuery;
      this.lastQuery = query;
      const hasQuery = query.trim().length > 0;
      this.error$.next(null);
      this.cache = null;
      if (queryChanged) {
        this.resetExpansion();
      }
      this.items$.next([]);
      this.isQueryLoading$.next(hasQuery);
      this.isSettled$.next(!hasQuery);
      if (!hasQuery) {
        this.status$.next(null);
      }
    }),
    debounceTime(DOCS_SEARCH_DEBOUNCE_MS),
    switchMap((query: string) => {
      const keyword = query.trim();
      type QueryResult = {
        query: string;
        docs: DocsSearchResult[];
        pdfHits: PdfPageSearchHit[];
        settled: boolean;
      };
      let out;
      if (!keyword) {
        this.status$.next(null);
        out = of<QueryResult | null>(null);
      } else {
        out = this.searchDocs(keyword).pipe(
          switchMap(({ docs, prefer, settled }) =>
            from(this.searchPdfPages(keyword, prefer)).pipe(
              catchError(() => {
                this.pdfSearchFailedUntil =
                  Date.now() + PDF_SEARCH_FAILURE_COOLDOWN_MS;
                this.status$.next({
                  i18nKey: 'com.streamlain.quicksearch.status.pdf-unavailable',
                });
                return of([] as PdfPageSearchHit[]);
              }),
              map(
                (pdfHits): QueryResult => ({
                  query: keyword,
                  docs,
                  pdfHits,
                  settled,
                })
              )
            )
          )
        );
      }
      return out.pipe(
        tap((data: QueryResult | null) => {
          this.cache = data;
          this.publishItems();
          this.isSettled$.next(data?.settled ?? true);
          this.isQueryLoading$.next(false);
        }),
        onStart(() => {
          this.error$.next(null);
          this.cache = null;
          this.items$.next([]);
          this.isQueryLoading$.next(Boolean(keyword));
          this.isSettled$.next(!keyword);
        }),
        catchError(err => {
          this.error$.next(err instanceof Error ? err.message : err);
          this.cache = null;
          this.resetExpansion();
          this.items$.next([]);
          this.isQueryLoading$.next(false);
          this.isSettled$.next(true);
          return EMPTY;
        }),
        onComplete(() => {})
      );
    })
  ) as Effect<string>;

  query(query: string) {
    this.queryEffect(query);
  }

  override dispose(): void {
    this.staleResultNudge.dispose();
    this.queryEffect.unsubscribe();
  }
}

function preferHighlightedTitle(
  searchTitle: string | undefined,
  displayTitle: string
) {
  return searchTitle?.includes('<b>') ? searchTitle : displayTitle;
}

function uniquePayloadSourceTypes(sources: readonly DocsSearchResult[]) {
  return [...new Set(sources.map(source => source.sourceType))];
}

function pdfHitBadge(
  hit: PdfPageSearchHit,
  fallback: I18nString | undefined
): I18nString | undefined {
  if (hit.technicalSourceKind === 'mail_storage') {
    return { i18nKey: 'com.streamlain.quicksearch.technical.mailStorage' };
  }
  return fallback;
}
